// LinkPreviewCard.tsx — 1.24.00 C1: the preview card under a post whose body
// carries a link.
//
// Three shapes, one component:
//   • external  → a bordered card that is itself the outbound <a>
//                 (target=_blank, rel="noopener noreferrer nofollow").
//   • internal  → the link points at a TruXSocial post: render OUR card and a
//                 react-router <Link to="/post/<id>">. Zero outbound requests,
//                 and the content is serialized live by the server, so a post
//                 removed after somebody linked it stops showing here too.
//   • tombstone → `{ unavailable: true }`, the viewer-relative "that internal
//                 post is gone for you" shape. Rendered by QuoteEmbed itself,
//                 not re-drawn here — one dashed tombstone, one place.
//
// Visual idiom is QuoteEmbed's on purpose (bordered embed, clamped text,
// thumbnail): a card under a post already means "this is somewhere else", and
// inventing a second look for that would just be noise.
//
// The remote `imageUrl` is loaded by the VIEWER's browser. That discloses the
// viewer's IP to the third-party host exactly as clicking the link would —
// accepted, stated, not proxied.
//
// The whole card is a single <a>/<Link>, which is also what keeps PostCard's
// onCardClick from double-navigating (it ignores clicks inside `a`).
//
// 1.24.00 C1 fix round: the SOURCE LINE IS THE HOST, ALWAYS. Title, description
// and site name are all copied verbatim from a page we do not control; only the
// URL says where the card goes. `siteName` is allowed to stand in for a missing
// title (a heading is understood to be the page's own words) and nowhere else —
// see core/lib/linkHost.ts.

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import type { LinkPreview, LinkPreviewUnavailable, Post } from '@/core/models/post';
import { postPath } from '@/core/lib/postUrl';
import { hostForDisplay } from '@/core/lib/linkHost';
import { Icon } from '@/icons';
import { QuoteEmbed } from './QuoteEmbed';
import { hasImages } from './postImages';

/**
 * The ONE rule for whether a post shows its link preview — shared by PostCard
 * and HeroPostCard so the feed and the detail page can never disagree.
 *
 * A post already carrying images, a poll, or a quoted post has a card of its
 * own; stacking a second one turns the feed into a wall of boxes. In that case
 * the link is still fully live — PostBody linkified it — it just doesn't get a
 * card as well.
 *
 * Returns the preview to render, or null.
 */
export function linkPreviewToShow(
  post: Pick<Post, 'media' | 'images' | 'poll' | 'quotedPost' | 'linkPreview'> &
    Partial<Pick<Post, 'video'>>,
): LinkPreview | LinkPreviewUnavailable | null {
  if (!post.linkPreview) return null;
  if (hasImages(post) || post.video || post.poll || post.quotedPost) return null;
  return post.linkPreview;
}

const cardStyle = `
  display: block;
  margin-top: 8px;
  padding: 10px 12px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.15s;
`;

const ExternalCard = styled.a`
  ${cardStyle}
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};


`;

const InternalCard = styled(Link)`
  ${cardStyle}
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-radius: ${({ theme }) => theme.radius.md};


`;

const SourceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

// Wraps rather than overflows: a host is length-capped in JS (MAX_HOST_CHARS)
// so it can't run away, but on a narrow column the remainder must fold onto a
// second line instead of being clipped — a clipped host hides its own tail,
// which is the part that says where the link goes.
const SourceText = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Title = styled.p`
  margin: 6px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textPrimary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Description = styled.p`
  margin: 4px 0 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Thumb = styled.img`
  margin-top: 8px;
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

export interface LinkPreviewCardProps {
  preview: LinkPreview | LinkPreviewUnavailable;
}

function isUnavailable(p: LinkPreview | LinkPreviewUnavailable): p is LinkPreviewUnavailable {
  return 'unavailable' in p && p.unavailable === true;
}

export function LinkPreviewCard({ preview }: LinkPreviewCardProps) {
  // The internal target is gone for THIS viewer — the same dashed tombstone a
  // quoted post gets, from the same component.
  if (isUnavailable(preview)) {
    return <QuoteEmbed quotedPost={{ unavailable: true }} />;
  }

  // NEVER preview.siteName: that is the linked page's own claim about who it is,
  // and a phishing page claims to be whoever it likes. The host is the one thing
  // on this card the author of the page cannot write.
  const source = hostForDisplay(preview.url);
  // siteName may stand in for a MISSING title — a heading reads as the page's
  // own words, so it cannot be mistaken for the domain on the line above.
  const heading = preview.title || preview.siteName || source;

  const inner = (
    <>
      <SourceRow>
        <Icon name="link" size={12} />
        <SourceText data-testid="link-preview-source">{source}</SourceText>
      </SourceRow>
      <Title>{heading}</Title>
      {preview.description && <Description>{preview.description}</Description>}
      {/* alt="" — the card's own text already carries the meaning; a decorative
          thumbnail announced twice is worse than one not announced at all. */}
      {preview.imageUrl && <Thumb src={preview.imageUrl} alt="" loading="lazy" />}
    </>
  );

  if (preview.internal) {
    return (
      <InternalCard
        // 1.24.00 D1.1: the preview carries the legacy id only (no wire change
        // here), so this stays a legacy path and the detail page relabels the
        // address bar on arrival — D1.2. Still bare in 1.24.50: a link preview
        // knows the linked post's id and nothing about who wrote it — the handle
        // on the SURROUNDING post is the person who shared the link, not its
        // author.
        to={postPath({ id: preview.internal.postId })}
        aria-label={`Linked post on TruXSocial: ${heading}`}
        data-testid="link-preview-internal"
      >
        {inner}
      </InternalCard>
    );
  }

  return (
    <ExternalCard
      href={preview.url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      aria-label={`${heading}, ${source} (opens in a new tab)`}
      data-testid="link-preview-card"
    >
      {inner}
    </ExternalCard>
  );
}
